'use client';

/**
 * GlobalLiveSwitch — the platform-wide live-trading master kill-switch, shown in
 * the topbar so it's always visible. OFF (default, safe) ⇒ NO subscription can
 * place a real order regardless of per-strategy arming. ON is a deliberate,
 * audited, typed-confirmation action; turning OFF is one click (the safe way).
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Power, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import {
  getLiveTrading,
  platformLiveQueryKey,
  setLiveTrading,
} from '@/lib/api/admin-platform';
import { cn } from '@/lib/util/cn';
import { useFormatTime } from '@/lib/display-tz';

export function GlobalLiveSwitch() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: platformLiveQueryKey,
    queryFn: ({ signal }) => getLiveTrading(signal),
    refetchInterval: 20_000,
  });
  const enabled = q.data?.data.enabled ?? null;
  const [open, setOpen] = React.useState(false);

  const m = useMutation({
    mutationFn: (v: { enabled: boolean; reason: string }) =>
      setLiveTrading(v.enabled, v.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: platformLiveQueryKey });
      setOpen(false);
    },
  });

  // Pending/unknown → neutral. ON → crit (loud). OFF → muted-safe.
  const tone =
    enabled === null
      ? 'border-border bg-surface-inset/60 text-fg-muted'
      : enabled
        ? 'border-crit/60 bg-crit-soft text-crit'
        : 'border-border bg-surface-inset/60 text-fg-muted';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Platform live-trading master switch"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-sm border px-2 py-1',
          'text-2xs font-semibold transition-colors',
          'hover:brightness-110',
          tone,
        )}
        aria-label="Global live-trading switch"
      >
        {enabled ? (
          <ShieldAlert className="h-3 w-3 stroke-[1.6]" aria-hidden />
        ) : (
          <Power className="h-3 w-3 stroke-[1.6]" aria-hidden />
        )}
        <span>Live</span>
        <span
          className={cn(
            'rounded-[3px] px-1 font-semibold',
            enabled ? 'bg-crit/20' : 'bg-surface',
          )}
        >
          {enabled === null ? '…' : enabled ? 'ON' : 'OFF'}
        </span>
      </button>

      {open ? (
        <SwitchDialog
          currentlyEnabled={enabled === true}
          pending={m.isPending}
          error={m.error}
          updatedBy={
            q.data?.data.updated_by_display_name
            ?? q.data?.data.updated_by_email
            ?? q.data?.data.updated_by
            ?? null
          }
          updatedAt={q.data?.data.updated_at ?? null}
          onClose={() => {
            m.reset();
            setOpen(false);
          }}
          onSubmit={(next, reason) => m.mutate({ enabled: next, reason })}
        />
      ) : null}
    </>
  );
}

function SwitchDialog({
  currentlyEnabled,
  pending,
  error,
  updatedBy,
  updatedAt,
  onClose,
  onSubmit,
}: {
  currentlyEnabled: boolean;
  pending: boolean;
  error: unknown;
  updatedBy: string | null;
  updatedAt: string | null;
  onClose: () => void;
  onSubmit: (next: boolean, reason: string) => void;
}) {
  const t = useFormatTime();
  const next = !currentlyEnabled;
  const [reason, setReason] = React.useState('');
  const [confirm, setConfirm] = React.useState('');

  // Turning ON is the dangerous direction → require typed confirmation.
  const needsTyped = next === true;
  const canSubmit =
    reason.trim().length >= 4 &&
    (!needsTyped || confirm.trim().toUpperCase() === 'ENABLE') &&
    !pending;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) {onClose();} }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {next ? (
              <ShieldAlert className="h-4 w-4 text-crit" />
            ) : (
              <Power className="h-4 w-4 text-fg-muted" />
            )}
            {next ? 'Enable platform live trading' : 'Disable platform live trading'}
          </DialogTitle>
          <DialogDescription>
            {next ? (
              <>
                Turns the <span className="font-mono text-fg">GLOBAL</span> master switch{' '}
                <span className="text-crit">ON</span>. Armed subscriptions can then place
                REAL orders. This is the platform-wide gate, not a per-strategy arm.
              </>
            ) : (
              <>
                Turns the global master switch <span className="font-mono text-fg">OFF</span> —
                halts NEW entries across every subscription. Exposure-reducing{' '}
                <span className="font-mono">EXITS</span> still go through so you can always get
                flat (no freeze trap). For a total block including exits, use{' '}
                <span className="font-mono text-crit">HARD FREEZE</span>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {updatedBy || updatedAt ? (
            <div className="text-2xs text-fg-subtle">
              last changed{updatedBy ? ` by ${updatedBy}` : ''}
              {updatedAt ? ` · ${t.datetime(updatedAt)}` : ''}
            </div>
          ) : null}

          {next ? (
            <div className="flex items-start gap-2 rounded border border-crit/50 bg-crit-soft px-3 py-2 text-2xs text-crit">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                With this ON, any armed deployment trades real money. Keep it OFF unless
                you are actively running live.
              </span>
            </div>
          ) : null}

          <label className="block">
            <span className="text-2xs font-medium text-fg-subtle">Reason (audited)</span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={next ? 'Why are you enabling live trading?' : 'Why are you disabling?'}
              rows={2}
              autoFocus
            />
          </label>

          {needsTyped ? (
            <label className="block">
              <span className="text-2xs font-medium text-fg-subtle">
                Type <span className="font-mono text-crit">ENABLE</span> to confirm
              </span>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="ENABLE"
                autoComplete="off"
              />
            </label>
          ) : null}

          {error ? (
            <div className="flex items-start gap-2 rounded border border-crit/50 bg-crit-soft px-3 py-2 text-2xs text-crit">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error instanceof Error ? error.message : 'Request failed'}</span>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button
            variant={next ? 'danger' : 'secondary'}
            size="sm"
            disabled={!canSubmit}
            onClick={() => onSubmit(next, reason.trim())}
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {next ? 'Enable live trading' : 'Disable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
